import { useCallback, useEffect, useRef, useState } from 'react'
import { Search as SearchIcon, Send } from 'lucide-react'
import {
    estimateQueuedSendMinutes,
    SMS_MAX_BODY_LENGTH,
    SMS_MAX_IMMEDIATE_RECIPIENTS,
    SMS_MAX_QUEUED_RECIPIENTS,
    SMS_QUEUE_DRAIN_PER_TICK,
    SMS_QUEUE_TICK_MINUTES,
} from '@numeo/types'
import { smsComposerState } from '@numeo/types-main-web-app'
import { Badge, Button, Checkbox, Modal, Spinner } from '@/components/ds2'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { SnackbarType } from '@/utils/snackbar'
import { BoxTruckVerification, fetchMyBoxTruckDrivers, queueSmsBroadcast, sendSmsToDrivers } from '../services/boxTruckLoadsApi'
import { SenderLineSelect, useSenderLines } from './SenderLineSelect'
import { collectRosterDriverIds } from '../utils/collectRosterDriverIds'
import { bulkSendOutcome, readBulkSend } from '../utils/smsSendOutcome'
import { gapPresetLabel, hasTelegram, isMailingReachable, isReachableOnlyOn } from '../utils/driverReachability'
import { bucketRecipients, smsFailureReason, trackBoxTruckSmsSendFailed, trackBoxTruckSmsSent, trackBoxTruckUiInteraction } from '../utils/analytics'

/**
 * Text several drivers the same thing at once — the app install instructions, a policy change.
 *
 * The roster is read a page at a time and searched server-side, the same way My Drivers and the Mailing
 * picker read it, so the whole fleet is reachable rather than the first fifty.
 *
 * Selectable means the number is one the sender will actually accept — `toE164Nanp`, the same rule the
 * server applies, not merely "the field is filled in". Those are different for exactly the values that
 * matter: `(202) 002-9292` and `(111) 222-3344` look like phones and are not, so offering them produced
 * a send that reported "1 without a phone" about a driver whose profile plainly showed one.
 *
 * Everyone the search matches can be selected at once, not only the rows on screen — a fleet-wide
 * message was otherwise fifty drivers at a time, sixteen times over. Past
 * `SMS_MAX_IMMEDIATE_RECIPIENTS` the send cannot finish inside the request, so it is QUEUED instead
 * and drained by the same tick that sends mailings: the whole selection still gets the message, over
 * the following minutes rather than at once. Both paths are the same button; only the wording changes,
 * because "it will arrive shortly" is a promise the operator has to be told about.
 */
const PAGE_SIZE = 50

export const SmsBulkModal = ({ onClose, onSent, onQueued }: { onClose: () => void; onSent: () => void; onQueued: () => void }) => {
    const { showSnackbar } = useSnackbar()
    const [drivers, setDrivers] = useState<BoxTruckVerification[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const search = useDebouncedValue(searchInput, 300)
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const senderLines = useSenderLines()
    const [senderPhoneNumberId, setSenderPhoneNumberId] = useState<string | null>(null)
    const [message, setMessage] = useState('')
    const [sending, setSending] = useState(false)
    const [selectingAll, setSelectingAll] = useState(false)
    /** The search whose every match is already selected, so the offer to do it again disappears. Any
     *  other change to the selection, or to the search, makes it stale. */
    const [coveredTerm, setCoveredTerm] = useState<string | null>(null)
    const seqRef = useRef(0)
    const selectAllSeqRef = useRef(0)
    /**
     * What is selected as of the last render, readable synchronously.
     *
     * The walk takes several round trips, so the value its closure captured is stale by the time it
     * lands — a driver ticked meanwhile was silently overwritten. Deciding inside a `setSelectedIds`
     * updater fixes that and breaks something else: React runs updaters later, so a cap checked in
     * there is read back before it was written, and a selection over the limit was accepted in silence.
     * A ref kept in step with the state answers both: fresh, and synchronous.
     */
    const selectedIdsRef = useRef<string[]>([])
    useEffect(() => {
        selectedIdsRef.current = selectedIds
    }, [selectedIds])

    const load = useCallback(
        async (nextPage: number, term: string, append: boolean) => {
            const seq = ++seqRef.current
            if (append) setLoadingMore(true)
            else setLoading(true)
            try {
                const { items, total: count } = await fetchMyBoxTruckDrivers(nextPage, PAGE_SIZE, term)
                if (seqRef.current !== seq) return
                const claimed = items.filter((d) => !!d.claimDriverId)
                setDrivers((prev) => (append ? [...prev, ...claimed] : claimed))
                setTotal(count)
                setPage(nextPage)
                setHasMore(nextPage * PAGE_SIZE < count)
            } catch (e) {
                if (seqRef.current === seq) showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Could not load the drivers')
            } finally {
                if (seqRef.current === seq) {
                    setLoading(false)
                    setLoadingMore(false)
                }
            }
        },
        [showSnackbar]
    )

    useEffect(() => {
        // A new search abandons a select-all still walking the pages of the old one; without this its
        // last page would land afterwards and select drivers the operator can no longer see.
        selectAllSeqRef.current++
        setCoveredTerm(null)
        void load(1, search, false)
    }, [load, search])

    // The same rule the Mailing picker greys rows out by — one definition, so the two pickers cannot
    // start disagreeing about who is textable.
    const reachable = (d: BoxTruckVerification) => isMailingReachable(d, 'sms')
    const selectableLoaded = drivers.filter(reachable)
    const allLoadedSelected = selectableLoaded.length > 0 && selectableLoaded.every((d) => selectedIds.includes(d.userId))

    const toggle = (userId: string) => {
        setCoveredTerm(null)
        setSelectedIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
    }

    /**
     * Select everyone the current search matches, not just the fifty on screen.
     *
     * The roster is paged, so this walks the pages — at the server's own maximum, so 800 drivers is
     * four reads rather than sixteen. It has to read the ROWS and not ids alone: whether a driver is
     * textable at all is decided from the row, by the same rule the list greys them out with, and a
     * selection that included the unreachable would promise sends that never happen.
     */
    const selectAllMatching = async (preset: 'all' | 'gap' = 'all') => {
        // `gap`: textable AND not on Telegram — the drivers an SMS is the only way to reach. The same
        // walk with one more test per row, so it costs no extra reads.
        const match = (d: BoxTruckVerification) => reachable(d) && (preset === 'all' || isReachableOnlyOn(d, 'sms'))
        setSelectingAll(true)
        const seq = ++selectAllSeqRef.current
        try {
            const { ids, skipped } = await collectRosterDriverIds(search, match, { cancelled: () => selectAllSeqRef.current !== seq })
            if (selectAllSeqRef.current !== seq) {
                // Abandoned because the operator changed the search while the pages were landing. Counted
                // like the other endings: a preset that is mostly abandoned is one that takes too long.
                trackBoxTruckUiInteraction('mailing_select_preset', `sms:${preset}:superseded`)
                return
            }
            // Merged over what is selected NOW, and judged before anything is set: over the cap, the
            // selection is left exactly as the operator left it and they are told why.
            const merged = [...new Set([...selectedIdsRef.current, ...ids])]
            if (merged.length > SMS_MAX_QUEUED_RECIPIENTS) {
                return showSnackbar(SnackbarType.ERROR, `One message can reach at most ${SMS_MAX_QUEUED_RECIPIENTS.toLocaleString()} drivers`)
            }
            setSelectedIds(merged)
            // Only a full select covers every match; the gap preset is a subset by construction, so the
            // offer to select the rest must stay on screen.
            setCoveredTerm(preset === 'all' ? search : null)
            trackBoxTruckUiInteraction('mailing_select_preset', `sms:${preset}:ok:${bucketRecipients(ids.length)}`)
            if (!ids.length)
                showSnackbar(
                    SnackbarType.WARNING,
                    preset === 'gap' ? 'Every driver you can text is already on Telegram' : 'None of these drivers has a number we can text'
                )
            else if (preset === 'gap') showSnackbar(SnackbarType.SUCCESS, `${ids.length} driver${ids.length === 1 ? '' : 's'} ${gapPresetLabel('sms')} added`)
            else if (skipped) showSnackbar(SnackbarType.INFO, `${ids.length} selected · ${skipped} skipped without a number we can text`)
        } catch (e) {
            if (selectAllSeqRef.current === seq) {
                // The walk is several reads: one of them failing is the thing that makes "select all"
                // unusable on a big fleet, and only the snackbar knew about it.
                trackBoxTruckUiInteraction('mailing_select_preset', `sms:${preset}:failed`)
                showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Could not select every driver')
            }
        } finally {
            if (selectAllSeqRef.current === seq) setSelectingAll(false)
        }
    }

    const toggleAllLoaded = () => {
        setCoveredTerm(null)
        setSelectedIds((prev) =>
            allLoadedSelected
                ? prev.filter((id) => !selectableLoaded.some((d) => d.userId === id))
                : [...new Set([...prev, ...selectableLoaded.map((d) => d.userId)])]
        )
    }

    const send = async () => {
        const text = message.trim()
        if (!text) return showSnackbar(SnackbarType.ERROR, 'Write the message first')
        if (!selectedIds.length) return showSnackbar(SnackbarType.ERROR, 'Pick at least one driver')
        setSending(true)
        try {
            if (selectedIds.length > SMS_MAX_IMMEDIATE_RECIPIENTS) return await queue(text)
            const result = await sendSmsToDrivers(selectedIds, text, undefined, senderPhoneNumberId)
            const verdict = readBulkSend(result, selectedIds)
            trackBoxTruckSmsSent({ surface: 'bulk', recipients: selectedIds.length, outcome: bulkSendOutcome(result), customSenderLine: !!senderPhoneNumberId })
            showSnackbar(verdict.handedOver ? SnackbarType.SUCCESS : SnackbarType.ERROR, verdict.summary)
            if (verdict.handedOver) {
                onSent()
                // Nothing that went out stays selected. The button was left enabled over the same
                // message and the same drivers, so one more click — or a second one while reading the
                // toast — texted all of them again, billed per segment. What Quo REFUSED stays
                // selected so a retry reaches exactly those; with nothing to retry there is no reason
                // for the modal to stay open.
                if (verdict.done) onClose()
                else setSelectedIds(verdict.retryable)
            }
        } catch (e) {
            const message = getUserErrorMessage(e)
            // By STATUS, not by reading the sentence shown to the operator. "rejected the API key",
            // "is rate-limiting this workspace" and "refused the request" all contain "Quo" and all
            // used to be filed as not_connected, so the one number this event exists to give was wrong.
            trackBoxTruckSmsSendFailed('bulk', smsFailureReason(e))
            showSnackbar(SnackbarType.ERROR, message || 'Could not send the messages')
        } finally {
            setSending(false)
        }
    }

    /**
     * Hand the selection over to be sent a slice at a time.
     *
     * The modal closes on success even though nothing has been delivered yet: the queue is the record
     * now, it survives this tab, and leaving the button armed over a selection that is already going
     * out is how the same fleet gets texted twice.
     */
    const queue = async (text: string) => {
        try {
            const { total } = await queueSmsBroadcast(selectedIds, text, senderPhoneNumberId)
            trackBoxTruckSmsSent({ surface: 'bulk', recipients: total, outcome: 'queued', customSenderLine: !!senderPhoneNumberId })
            showSnackbar(SnackbarType.SUCCESS, `Queued for ${total.toLocaleString()} drivers — about ${estimateQueuedSendMinutes(total)} minutes`)
            // Not `onSent`: nothing has been sent yet, so there are no new conversations to reload.
            // What the page must do is start watching the queue empty.
            onQueued()
            onClose()
        } catch (e) {
            trackBoxTruckSmsSendFailed('bulk', smsFailureReason(e))
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Could not queue the messages')
        }
    }

    // One counter for every composer (`@numeo/types-main-web-app`): a character outside GSM-7 — an
    // emoji, a pasted curly apostrophe — switches the message to UCS-2 and multiplies what every
    // recipient costs. Refused here rather than warned about: this modal sends to the whole list.
    // ONE CALL from the owner, which also normalizes: this modal posts a textarea as typed, so nothing
    // here passed through `htmlToSmsText` and a pasted tab or non-breaking space disabled Send over a
    // character nobody can see. The endpoint runs the same function, so both sides judge one string.
    const { cost, blocked, priceHint, segmentWarning } = smsComposerState(message)
    const segments = cost.segments
    const queued = selectedIds.length > SMS_MAX_IMMEDIATE_RECIPIENTS

    return (
        <Modal open onClose={onClose} size="lg" header={<h2 className="text-base font-semibold text-foreground">New message</h2>}>
            <div className="space-y-3">
                <div>
                    <label className="text-xs font-medium text-foreground-muted">Message</label>
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value.slice(0, SMS_MAX_BODY_LENGTH))}
                        rows={4}
                        placeholder="Download the Numeo driver app: …"
                        disabled={sending}
                        className="mt-1 w-full resize-y rounded-xl bg-surface px-3 py-2 text-sm text-foreground shadow-field outline-none placeholder:text-foreground-muted/60 focus:ring-2 focus:ring-accent"
                    />
                    {blocked && <p className="mt-1 text-[11px] font-medium text-danger">{blocked}</p>}
                    {priceHint && <p className="mt-1 text-[11px] text-foreground-muted">{priceHint}</p>}
                    {segmentWarning && (
                        <p className="mt-1 text-[11px] font-medium text-warning">
                            {segments} segments per driver — shorten it, or this one message costs {segments}× what it looks like.
                        </p>
                    )}
                    <div className="mt-1 flex items-center justify-between text-[11px] tabular-nums text-foreground-muted/70">
                        <span>
                            {selectedIds.length.toLocaleString()} driver{selectedIds.length === 1 ? '' : 's'} selected
                            {selectedIds.length > 0 &&
                                segments > 0 &&
                                ` · ${(segments * selectedIds.length).toLocaleString()} segment${segments * selectedIds.length === 1 ? '' : 's'} billed to your Quo balance`}
                        </span>
                        <span>
                            {message.length}/{SMS_MAX_BODY_LENGTH}
                        </span>
                    </div>
                </div>

                <SenderLineSelect lines={senderLines} value={senderPhoneNumberId} disabled={sending} onChange={setSenderPhoneNumberId} />

                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                        <label className="text-xs font-medium text-foreground-muted">
                            Drivers <span className="font-normal">({loading ? '…' : total.toLocaleString()})</span>
                        </label>
                        {!loading && total > drivers.length && coveredTerm !== search && (
                            <Button variant="ghost" size="sm" loading={selectingAll} disabled={sending} onClick={() => void selectAllMatching('all')}>
                                {search ? `Select all ${total.toLocaleString()} matching` : `Select all ${total.toLocaleString()}`}
                            </Button>
                        )}
                        {/* The drivers Telegram cannot carry. This modal exists mostly for them — the app
                            instructions, the nudge to connect a group — and picking them by hand meant
                            reading a column the modal did not even show. */}
                        {!loading && total > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                loading={selectingAll}
                                disabled={sending}
                                title="Select everyone you can text who has no connected Telegram group"
                                onClick={() => void selectAllMatching('gap')}
                            >
                                Select all {gapPresetLabel('sms')}
                            </Button>
                        )}
                        {selectedIds.length > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                disabled={sending || selectingAll}
                                onClick={() => {
                                    setCoveredTerm(null)
                                    setSelectedIds([])
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </div>
                    <div className="relative">
                        <SearchIcon size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-muted" />
                        <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search name, phone, email…"
                            className="h-8 w-56 rounded-lg border border-border bg-background pl-7 pr-2 text-xs text-foreground outline-none placeholder:text-foreground-muted/60 focus:border-accent"
                        />
                    </div>
                </div>

                <div className="max-h-[42vh] overflow-y-auto rounded-xl border border-border">
                    {loading ? (
                        <div className="flex justify-center py-10">
                            <Spinner color="accent" size={20} />
                        </div>
                    ) : drivers.length === 0 ? (
                        <p className="p-4 text-xs text-foreground-muted">No drivers found.</p>
                    ) : (
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-border bg-default/40 text-xs text-foreground-muted">
                                    <th className="w-9 px-3 py-2">
                                        <Checkbox
                                            checked={allLoadedSelected}
                                            disabled={selectableLoaded.length === 0}
                                            onChange={toggleAllLoaded}
                                            ariaLabel="Select the loaded drivers"
                                        />
                                    </th>
                                    <th className="px-2 py-2 font-medium">Driver</th>
                                    <th className="px-2 py-2 font-medium">Phone</th>
                                    <th className="px-2 py-2 font-medium text-foreground-muted/70">Telegram</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {drivers.map((d) => {
                                    const can = reachable(d)
                                    const name = d.boxTruckInfo.info?.name || d.name || d.email
                                    return (
                                        <tr
                                            key={d.userId}
                                            onClick={() => can && toggle(d.userId)}
                                            className={
                                                can
                                                    ? `cursor-pointer ${selectedIds.includes(d.userId) ? 'bg-accent-soft/30' : 'hover:bg-default/40'}`
                                                    : 'opacity-70'
                                            }
                                        >
                                            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                                <Checkbox
                                                    checked={selectedIds.includes(d.userId)}
                                                    disabled={!can}
                                                    onChange={() => toggle(d.userId)}
                                                    ariaLabel={`Select ${name}`}
                                                />
                                            </td>
                                            <td className="max-w-[220px] truncate px-2 py-2">{name}</td>
                                            <td className="px-2 py-2">
                                                {can ? (
                                                    <span className="text-xs text-foreground-muted">{d.boxTruckInfo.info?.phone}</span>
                                                ) : d.boxTruckInfo.info?.phone ? (
                                                    // There IS a value — say that it is not a number anyone can text, so the
                                                    // fix (edit the profile) is obvious rather than mysterious.
                                                    <span className="inline-flex items-center gap-2">
                                                        <span className="text-xs text-foreground-muted/70 line-through">{d.boxTruckInfo.info.phone}</span>
                                                        <Badge variant="soft" type="danger" label="Not a US number" />
                                                    </span>
                                                ) : (
                                                    <Badge variant="soft" type="danger" label="No phone" />
                                                )}
                                            </td>
                                            {/* Not a reason the row is disabled — it says whether this text is the
                                                only way to reach them, which is what the gap preset selects on. */}
                                            <td className="px-2 py-2">
                                                {hasTelegram(d) ? (
                                                    <Badge variant="soft" type="default" label="On Telegram" />
                                                ) : (
                                                    <span className="text-xs text-foreground-muted/60">Not on Telegram</span>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                    {hasMore && !loading && (
                        <div className="flex justify-center py-2">
                            <Button variant="ghost" size="sm" loading={loadingMore} onClick={() => void load(page + 1, search, true)}>
                                Load more
                            </Button>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] leading-snug text-foreground-muted/80">
                        {queued
                            ? `More than ${SMS_MAX_IMMEDIATE_RECIPIENTS} at a time cannot go out in one go — this is queued and sent at about ${SMS_QUEUE_DRAIN_PER_TICK} drivers every ${SMS_QUEUE_TICK_MINUTES} minutes, so roughly ${estimateQueuedSendMinutes(selectedIds.length)} minutes for this many. You can close the page.`
                            : ''}
                    </p>
                    <div className="flex shrink-0 justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            loading={sending}
                            disabled={!message.trim() || !selectedIds.length || selectingAll || !!blocked}
                            prefix={<Send size={14} />}
                            onClick={() => void send()}
                        >
                            {queued ? 'Queue for' : 'Send to'} {selectedIds.length.toLocaleString()}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
